import {BASE_URL} from 'appConstants';

import {SCHEDULE_ITEM_TYPES} from 'ducks/tenancy';
import {transformFiltersForStoreToFiltersForApi} from 'utils/filters';

import {get, post} from 'sdk';

export const fetchTenancy = ({tenancyId}) => {
  const url = `${BASE_URL}/v1/living/tenancy/${tenancyId}/`;
  return get(url);
};

export const fetchSchedule = ({tenancyId, scheduleType}) => {
  const url = `${BASE_URL}/v1/payments/schedule/${scheduleType}/${tenancyId}/`;
  return get(url);
};

export const fetchInvoice = ({invoiceId}) => {
  const url = `${BASE_URL}/v1/payments/schedule/invoice/${invoiceId}/`;
  return get(url);
};

export const fetchDraftInvoice = ({draftInvoiceId}) => {
  const url = `${BASE_URL}/v1/payments/invoices/detail/draft/${draftInvoiceId}/`;
  return get(url);
};

export const addPayment = ({
  invoice,
  amount,
  datePaid,
  paidTo,
  reference,
  overpaymentDescription,
  batchNumber,
  creditCardNumber,
  colabAccountType,
  tenancyId
}) => {
  const url = `${BASE_URL}/v1/payments/invoice/${tenancyId}/add-payment/`;
  const payload = {
    invoice,
    amount,
    datePaid,
    paidTo,
    reference,
    overpaymentDescription,
    batchNumber,
    creditCardNumber,
    colabAccountType
  };

  return post(url, payload);
};

export const fetchCreditNote = ({creditNoteId}) => {
  const url = `${BASE_URL}/v1/payments/schedule/credit-note/${creditNoteId}/`;
  return get(url);
};

export const addRefund = ({
  creditNote,
  amount,
  datePaid,
  paidTo,
  reference,
  tenancyId
}) => {
  const url = `${BASE_URL}/v1/payments/credit-note/${tenancyId}/add-refund/`;
  const payload = {
    creditNote,
    amount,
    datePaid,
    paidTo,
    reference
  };
  return post(url, payload);
};

export const fetchPayment = ({paymentId}) => {
  const url = `${BASE_URL}/v1/payments/schedule/payment/${paymentId}/`;
  return get(url);
};

export const getNextFreeIdCounter = ({tenancyId, objectType}) => {
  let objectUri;
  if (objectType === SCHEDULE_ITEM_TYPES.INVOICE) {
    objectUri = 'invoice';
  }
  if (objectType === SCHEDULE_ITEM_TYPES.CREDIT_NOTE) {
    objectUri = 'credit-note';
  }

  const url = `${BASE_URL}/v1/payments/${objectUri}/${tenancyId}/next-free-id-counter/`;
  return get(url);
};

export const fetchXeroAccounts = ({location, accountType}) => {
  const url = `${BASE_URL}/v1/payments/xero-accounts/`;

  return get(url, {location, accountType});
};

export const fetchXeroTaxRates = ({filters}) => {
  const url = `${BASE_URL}/v1/payments/xero-tax-rates/`;
  const params = {...transformFiltersForStoreToFiltersForApi(filters)};

  return get(url, params);
};

export const fetchCanCreateFinanceItemForPastMonths = ({locationRef}) => {
  const url = `${BASE_URL}/v1/payments/${locationRef}/can-create-finance-item-for-past-months/`;

  return get(url);
};

export const fetchXeroOrganisation = ({locationRef}) => {
  const url = `${BASE_URL}/v1/payments/xero/${locationRef}/`;
  return get(url);
};

export const fetchRoomAllocations = ({tenancyId}) => {
  const url = `${BASE_URL}/v2/living/tenancy/${tenancyId}/room-allocations/`;
  return get(url);
};

export const fetchInvoiceAllocatedCreditNotesXeroLinks = ({
  tenancyId,
  invoiceId
}) => {
  const url = `${BASE_URL}/v1/payments/invoice/${tenancyId}/unallocated-credit-notes-xero-links/${invoiceId}/`;
  return get(url);
};

export const roomMove = ({
  moveInDate,
  moveInHour,
  vacateDate,
  vacateHour,
  allocation,
  booking,
  room,
  cleanupPeriodDays,
  locationRef,
  reason
}) => {
  const url = `${BASE_URL}/v1/living/${locationRef}/rooms/move/`;
  const payload = {
    moveInDate,
    moveInHour,
    vacateDate,
    vacateHour,
    allocation,
    booking,
    room,
    cleanupPeriodDays,
    reason
  };

  return post(url, payload);
};

export const findAllocation = ({
  bookingId,
  startingPoint,
  startingHour,
  locationRef
}) => {
  const url = `${BASE_URL}/v1/living/${locationRef}/rooms/find-allocation/`;
  const payload = {
    booking: bookingId,
    startingPoint,
    startingHour
  };

  return post(url, payload);
};

export const createInvoice = ({
  idCounter,
  unitPrice,
  quantity,
  discount,
  taxRate,
  description,
  reference,
  account,
  dueDate,
  start,
  end,
  dateIssued,
  location,
  scheduleType,
  tenancyId
}) => {
  const url = `${BASE_URL}/v1/payments/invoice/${tenancyId}/${scheduleType}/create/`;
  const payload = {
    idCounter,
    unitPrice,
    quantity,
    discount,
    taxRate,
    description,
    reference,
    account,
    dueDate,
    start,
    end,
    dateIssued,
    location
  };

  return post(url, payload);
};

export const createDraftInvoice = ({
  location,
  xeroTaxRate,
  xeroAccount,
  dateIssued,
  dueDate,
  rentReference,
  rentStart,
  rentEnd,
  itemDescription,
  quantity,
  unitPrice,
  schedule
}) => {
  const url = `${BASE_URL}/v1/payments/invoices/create/draft/`;
  const payload = {
    location,
    xeroTaxRate,
    xeroAccount,
    dateIssued,
    dueDate,
    rentReference,
    rentStart,
    rentEnd,
    itemDescription,
    quantity,
    unitPrice,
    schedule
  };
  return post(url, payload);
};

export const deleteDraftInvoice = ({draftInvoiceId}) => {
  const url = `${BASE_URL}/v1/payments/invoices/delete/draft/${draftInvoiceId}/`;
  return post(url);
};

export const approveDraftInvoice = ({draftInvoiceId}) => {
  const url = `${BASE_URL}/v1/payments/invoices/approve/draft/${draftInvoiceId}/`;
  return post(url);
};

export const fetchOverpayment = ({overpaymentId}) => {
  const url = `${BASE_URL}/v1/payments/overpayment/${overpaymentId}/`;
  return get(url);
};

export const createCreditNote = ({
  idCounter,
  quantity,
  unitPrice,
  discount,
  taxRate,
  account,
  description,
  dateIssued,
  rentStart,
  rentEnd,
  reference,
  location,
  scheduleType,
  tenancyId,
  invoice
}) => {
  const url = `${BASE_URL}/v1/payments/credit-note/${tenancyId}/${scheduleType}/create/`;
  let payload: any = {
    idCounter,
    quantity,
    unitPrice,
    discount,
    taxRate,
    account,
    description,
    dateIssued,
    rentStart,
    rentEnd,
    reference,
    location
  };

  if (invoice) {
    payload.invoice = invoice;
  }

  return post(url, payload);
};

export const addRefundForOverpayment = ({
  amount,
  overpayment,
  paymentDate,
  reference,
  xeroAccount,
  tenancyId
}) => {
  const url = `${BASE_URL}/v1/payments/overpayment/${tenancyId}/add-refund/`;
  const payload = {
    amount,
    overpayment,
    paymentDate,
    reference,
    xeroAccount
  };

  return post(url, payload);
};

export const recordBreakClauseNotice = ({
  givenDate,
  departureDate,
  tenancyId,
  warnings
}) => {
  const url = `${BASE_URL}/v1/living/tenancy/${tenancyId}/break-clause/create-notice/`;
  const payload = {
    givenDate,
    departureDate,
    warnings
  };
  return post(url, payload);
};

export const createOverpayment = ({
  amount,
  description,
  paymentDate,
  xeroAccount,
  scheduleType,
  tenancyId
}) => {
  const url = `${BASE_URL}/v1/payments/overpayment/${tenancyId}/${scheduleType}/create/`;
  const payload = {
    amount,
    description,
    paymentDate,
    xeroAccount
  };

  return post(url, payload);
};

export const voidObject = ({objectType, objectId}) => {
  let objectUri;

  if (objectType === SCHEDULE_ITEM_TYPES.INVOICE) {
    objectUri = 'invoice';
  }
  if (objectType === SCHEDULE_ITEM_TYPES.CREDIT_NOTE) {
    objectUri = 'credit-note';
  }
  if (
    objectType === SCHEDULE_ITEM_TYPES.PAYMENT ||
    objectType === SCHEDULE_ITEM_TYPES.REFUND
  ) {
    objectUri = 'transaction';
  }
  if (objectType === SCHEDULE_ITEM_TYPES.OVERPAYMENT) {
    objectUri = 'overpayment';
  }

  const url = `${BASE_URL}/v1/payments/void/${objectUri}/${objectId}/`;

  return post(url);
};

export const deallocateCreditNotes = ({invoiceId, tenancyId}) => {
  const url = `${BASE_URL}/v1/payments/invoice/${tenancyId}/unallocate-credit-notes/`;
  const payload = {invoice: invoiceId};

  return post(url, payload);
};

export const deallocateCreditNoteFromInvoice = ({
  invoiceId,
  tenancyId,
  creditNoteId
}) => {
  const url = `${BASE_URL}/v1/payments/credit-note/${tenancyId}/unallocate-from-invoice/`;
  const payload = {invoice: invoiceId, creditNote: creditNoteId};

  return post(url, payload);
};

export const addXeroId = ({xeroId, objectId, objectType}) => {
  let objectUri;
  if (objectType === SCHEDULE_ITEM_TYPES.CREDIT_NOTE) {
    objectUri = 'credit-note';
  } else {
    objectUri = objectType;
  }

  const url = `${BASE_URL}/v1/payments/add-xero-id/${objectUri}/${objectId}/`;
  const payload = {xeroId};

  return post(url, payload);
};

export const syncWithXero = ({objectType, objectId}) => {
  let objectUri;
  if (objectType === SCHEDULE_ITEM_TYPES.CREDIT_NOTE) {
    objectUri = 'credit-note';
  } else {
    objectUri = objectType;
  }
  const url = `${BASE_URL}/v1/payments/sync/${objectUri}/${objectId}/`;

  return post(url);
};

export const fetchBreakClause = ({tenancyId}) => {
  const url = `${BASE_URL}/v2/living/tenancy/${tenancyId}/break-clause/`;
  return get(url);
};

export const fetchTerminationReasons = ({tenancyId}) => {
  const url = `${BASE_URL}/v1/living/tenancy/${tenancyId}/early-termination/reasons/`;
  return get(url);
};

export const fetchGocardlessCustomer = ({gocardlessCustomerId}) => {
  const url = `${BASE_URL}/v1/payments/gocardless/customers/retrieve/${gocardlessCustomerId}`;
  return get(url);
};

export const fetchGocardlessCustomerPayments = ({gocardlessCustomerId}) => {
  const url = `${BASE_URL}/v1/payments/gocardless/customer/${gocardlessCustomerId}/payments/`;
  return get(url);
};

export const validateTenancyCanBeTerminated = ({
  tenancyId,
  departureDate,
  terminationDate,
  terminatedBeforeMoveIn
}) => {
  const url = `${BASE_URL}/v1/living/tenancy/${tenancyId}/early-termination/validate/`;

  const payload = {
    departureDate,
    terminationDate,
    terminatedBeforeMoveIn
  };

  return post(url, payload);
};

export const initiateGocardlessPayment = ({
  invoiceId,
  description,
  specifiedChargeDate
}) => {
  const url = `${BASE_URL}/v1/payments/gocardless/initiate-payment/`;
  const payload = {invoice: invoiceId, description, specifiedChargeDate};

  return post(url, payload);
};

export const fetchGocardlessPayment = ({gocardlessPaymentId}) => {
  const url = `${BASE_URL}/v1/payments/gocardless/payments/${gocardlessPaymentId}/`;
  return get(url);
};

export const fetchGocardlessPaymentEventsHistory = ({gocardlessPaymentId}) => {
  const url = `${BASE_URL}/v1/payments/gocardless/payments/${gocardlessPaymentId}/events-history/`;
  return get(url);
};

export const predictTermination = ({
  tenancyId,
  reason,
  terminationDate,
  departureDate,
  notes,
  terminatedBeforeMoveIn
}) => {
  const url = `${BASE_URL}/v1/living/tenancy/${tenancyId}/early-termination/predict/`;
  const payload = {
    reason,
    terminationDate,
    departureDate,
    notes,
    terminatedBeforeMoveIn
  };

  return post(url, payload);
};

export const filterGocardlessCustomers = ({locationRef, query}) => {
  const url = `${BASE_URL}/v1/payments/gocardless/filter-customers/`;
  const payload = {location: locationRef, query};

  return post(url, payload);
};

export const filterGocardlessActiveMandates = ({locationRef, customerId}) => {
  const url = `${BASE_URL}/v1/payments/gocardless/filter-active-mandates/`;
  const payload = {location: locationRef, customerId};

  return post(url, payload);
};

export const confirmTermination = ({
  tenancyId,
  notes,
  reason,
  departureDate,
  terminationDate,
  terminatedBeforeMoveIn
}) => {
  const url = `${BASE_URL}/v1/living/tenancy/${tenancyId}/early-termination/create/`;
  const payload = {
    notes,
    reason,
    departureDate,
    terminationDate,
    terminatedBeforeMoveIn
  };

  return post(url, payload);
};

export const cancelGocardlessPayment = ({gocardlessPaymentId}) => {
  const url = `${BASE_URL}/v1/payments/gocardless/payments/${gocardlessPaymentId}/cancel/`;
  return post(url);
};

export const retryGocardlessPayment = ({gocardlessPaymentId}) => {
  const url = `${BASE_URL}/v1/payments/gocardless/payments/${gocardlessPaymentId}/retry/`;
  return post(url);
};

export const moveEntireBooking = ({
  locationRef,
  booking,
  newRoom,
  createCleanup,
  cleanupPeriodDays
}) => {
  const url = `${BASE_URL}/v1/living/${locationRef}/allocations/move-booking/`;
  const payload = {booking, newRoom, createCleanup, cleanupPeriodDays};

  return post(url, payload);
};

export const fetchEarlyTerminations = ({tenancyId}) => {
  const url = `${BASE_URL}/v1/living/tenancy/${tenancyId}/early-termination/list/`;
  return get(url);
};

export const checkInTenants = ({tenancyId}) => {
  const url = `${BASE_URL}/v1/living/tenancy/${tenancyId}/check-in/`;
  return post(url);
};

export const undoCheckInTenants = ({bookingId}) => {
  const url = `${BASE_URL}/v1/living/tenancy/${bookingId}/undo-check-in/`;
  return post(url);
};

export const checkOutTenants = ({tenancyId}) => {
  const url = `${BASE_URL}/v1/living/tenancy/${tenancyId}/check-out/`;
  return post(url);
};

export const undoCheckOutTenants = ({bookingId}) => {
  const url = `${BASE_URL}/v1/living/tenancy/${bookingId}/undo-check-out/`;
  return post(url);
};

export const getMoveRoomOverlap = ({
  locationRef,
  moveInDate,
  moveInHour,
  vacateDate,
  vacateHour
}) => {
  const url = `${BASE_URL}/v1/living/${locationRef}/rooms/move-room-overlap/`;
  const payload = {
    moveInDate,
    moveInHour,
    vacateDate,
    vacateHour
  };
  return post(url, payload);
};

export const deleteEarlyTermination = ({tenancyId, terminationId}) => {
  const url = `${BASE_URL}/v1/living/tenancy/${tenancyId}/early-termination/delete/`;
  const payload = {
    id: terminationId
  };

  return post(url, payload);
};

export const fetchNextPossibleChargeDate = ({tenancyId}) => {
  const url = `${BASE_URL}/v1/payments/gocardless/${tenancyId}/next-possible-charge-date/`;

  return get(url);
};

export const fetchGocardlessCustomerAndInvitation = ({tenancyId}) => {
  const url = `${BASE_URL}/v1/payments/gocardless/${tenancyId}/current-customer-and-invitation/`;

  return get(url);
};

export const removeGocardlessInvitation = ({
  tenancyId,
  invitationId,
  voidInvitation
}) => {
  const url = `${BASE_URL}/v1/payments/gocardless/invitations/${tenancyId}/detach/`;
  const payload = {gocardlessInvitation: invitationId, voidInvitation};

  return post(url, payload);
};

export const removeGocardlessCustomer = ({tenancyId, customerId}) => {
  const url = `${BASE_URL}/v1/payments/gocardless/customers/${tenancyId}/detach/`;
  const payload = {gocardlessCustomer: customerId};

  return post(url, payload);
};

export const voidGocardlessInvitationWarnings = ({invitationId}) => {
  const url = `${BASE_URL}/v1/payments/gocardless/invitations/${invitationId}/void-warnings/`;

  return post(url);
};

export const fetchGoCardlessTenantsForBooking = ({bookingId}) => {
  const url = `${BASE_URL}/v1/payments/gocardless/tenants-for-booking/${bookingId}/`;

  return get(url);
};

export const fetchSendGoCardlessInvitationWarnings = ({
  tenancyId,
  tenantId
}) => {
  const url = `${BASE_URL}/v1/payments/gocardless/invitations/${tenancyId}/send-warnings/`;
  const payload = {tenant: tenantId};

  return post(url, payload);
};

export const sendGoCardlessInvitation = ({tenancyId, tenantId}) => {
  const url = `${BASE_URL}/v1/payments/gocardless/invitations/${tenancyId}/send/`;
  const payload = {tenant: tenantId};

  return post(url, payload);
};

export const fetchLinkGoCardlessMandateToBookingWarnings = ({
  gocardlessCustomerId,
  gocardlessMandateId
}) => {
  const url = `${BASE_URL}/v1/payments/gocardless/link-customer-and-mandate-to-booking-warnings/`;

  const payload = {
    gocardlessCustomerId,
    gocardlessMandateId
  };

  return post(url, payload);
};

export const connectGoCardlessCustomer = ({
  goCardlessCustomer,
  goCardlessMandate,
  tenant,
  tenancyId
}) => {
  const url = `${BASE_URL}/v1/payments/gocardless/${tenancyId}/link-mandate-to-customer/`;
  const payload = {goCardlessCustomer, goCardlessMandate, tenant};

  return post(url, payload);
};

export const fetchSuggestedGoCardlessCustomersAndInvitations = ({
  tenancyId
}) => {
  const url = `${BASE_URL}/v1/payments/gocardless/${tenancyId}/suggested-gocardless-customers-and-invitations/`;

  return get(url);
};

export const attachGoCardlessCustomerToBooking = ({
  tenancyId,
  goCardlessCustomer
}) => {
  const url = `${BASE_URL}/v1/payments/gocardless/customers/${tenancyId}/attach/`;
  const payload = {gocardlessCustomer: goCardlessCustomer};

  return post(url, payload);
};

export const attachGoCardlessInvitationToBooking = ({
  tenancyId,
  goCardlessInvitation
}) => {
  const url = `${BASE_URL}/v1/payments/gocardless/invitations/${tenancyId}/attach/`;
  const payload = {gocardlessInvitation: goCardlessInvitation};

  return post(url, payload);
};

export const cancelGocardlessCustomer = ({gocardlessCustomerId}) => {
  const url = `${BASE_URL}/v1/payments/gocardless/customers/${gocardlessCustomerId}/cancel/`;

  return post(url, {});
};

export const cancelGocardlessCustomerWarnings = ({gocardlessCustomerId}) => {
  const url = `${BASE_URL}/v1/payments/gocardless/customers/${gocardlessCustomerId}/cancel-warnings/`;

  return post(url, {});
};
